<template>
  <ClientOnly>
    <Overlay gtm-overlay-name="vacancy">
      <div>
        <div class="font-serif font-bold text-[clamp(1.5rem,-1.4831rem+14.9153vw,4.25rem)] text-purple-dark leading-none">
          Solliciteren
        </div>

        <div class="prose mt-4">
          Zit je met vragen? Bel of stuur ons een e-mail, we staan je graag te woord.
        </div>

        <Form
          class="mt-12"
          handle="vacancy"
          :form="form"
          :redirect-url="block.entry_after_submit?.url"
          :additional-fields="{
            'current_url': useRoute().path,
          }"
          :submit-button-text="$statamic.trans('form_vacancy_submit_button_text')"
        />
      </div>

      <div
        v-if="employees?.length"
        class="flex flex-col items-center self-start p-6 w-full max-w-md text-center bg-purple-dark rounded-3xl md:p-12 md:rounded-5xl lg:sticky lg:top-0"
      >
        <div class="flex mx-auto sm:mx-0">
          <div
            v-for="(employee, index) in employees"
            :key="employee.id"
            :class="`size-32 overflow-hidden rounded-full border-2 border-white bg-gradient-employee ${index !== 0 ? '-ml-6' : ''} xl:size-36`"
          >
            <img
              v-if="employee.image"
              :src="employee.image.sm.url"
              :alt="employee.image.sm.alt"
              class="object-cover"
              :width="employee.image.sm.width"
              :height="employee.image.sm.height"
              loading="lazy"
              draggable="false"
            >
          </div>
        </div>

        <div class="flex flex-col mt-6 md:mt-8">
          <div v-if="names" class="font-heavy text-white text-xl">
            {{ names.join(' & ') }}
          </div>

          <div v-if="positions" class="mt-1 font-serif font-medium text-white text-xs md:text-sm">
            {{ uniquePositions.join(' & ') }}{{ duplicatePositions.length > 0 ? 's' : '' }}
          </div>

          <a
            v-if="employees?.length === 1 && employees[0].email"
            :href="`mailto:${employees[0].email}`"
            class="mt-2 text-white text-xs underline underline-offset-2 md:mt-3 md:text-sm"
          >
            {{ employees[0].email }}
          </a>

          <div
            v-if="block.call_to_action?.text"
            v-html="nl2br(block.call_to_action?.text)"
            class="mt-4 text-white text-sm"
          />

          <div
            v-if="block.call_to_action?.whatsapp_number || block.call_to_action?.email || block.call_to_action?.phone_number"
            class="flex items-center justify-center gap-4 mt-auto pt-6"
          >
            <a
              v-if="block.call_to_action?.whatsapp_number"
              :href="block.call_to_action.whatsapp_number"
              :aria-label="$statamic.trans('a11y_contact_us_via_whatsapp')"
              class="inline-flex items-center justify-center size-11 text-white border border-white/25 rounded-full"
            >
              <Icon icon="svgo-whatsapp" class="h-5 w-5" />
            </a>

            <a
              v-if="block.call_to_action?.email"
              :href="`mailto:${block.call_to_action.email}`"
              :aria-label="$statamic.trans('a11y_contact_us_via_email')"
              class="inline-flex items-center justify-center size-11 text-white border border-white/25 rounded-full"
            >
              <Icon icon="svgo-mail-o" class="h-5 w-5" />
            </a>

            <a
              v-if="block.call_to_action?.phone_number"
              :href="`tel:${block.call_to_action.phone_number}`"
              :aria-label="$statamic.trans('a11y_contact_us_via_phone')"
              class="inline-flex items-center justify-center size-11 text-white border border-white/25 rounded-full"
            >
              <Icon icon="svgo-phone" class="h-5 w-5" />
            </a>
          </div>
        </div>
      </div>
    </Overlay>
  </ClientOnly>
</template>

<script setup lang="ts">
import type { Entry } from '~/statamic/types/entry';
import type { VacancyApplyForm } from '~/statamic/types/block';

const props = defineProps<{
  entry: Entry;
  block: VacancyApplyForm;
}>();

const form = await useStatamicForm({ handle: 'vacancy' });

const employees = props.block.call_to_action?.employees ?? [];
const names = employees?.map((employee) => employee.title);
const positions = employees?.map((employee) => employee.position);
const uniquePositions = Array.from(new Set(positions));
const duplicatePositions = positions.filter((position) => {
  return positions.indexOf(position) !== positions.lastIndexOf(position);
});
</script>
